import { api } from "./client";

export interface ArenaRankEntry {
  rank: number;
  user_id: number | null;
  name: string;
  br: number;
  class: string;
  level: number;
  is_npc: boolean;
  can_fight: boolean;
  fight_block_reason: string | null;
}

export interface SoloArenaRules {
  rank_attack_above: number;
  rank_attack_below: number;
  unranked_attack_bottom: number;
}

export interface SoloRanksResponse {
  ranks: ArenaRankEntry[];
  my_rank: number | null;
  daily_attempts: number;
  last_attack: string | null;
  skip_cooldown_diamonds: number;
  vip_skip_free?: boolean;
  solo_cooldown_exempt?: boolean;
  attempt_cooldown_sec?: number;
  rules: SoloArenaRules;
}

export interface AttackSoloResponse {
  battle_id: number;
  result: string;
  rewards: Record<string, number>;
  new_rank: number | null;
  info_messages: string[];
}

export interface PartyWindow {
  hour: number;
  minute: number;
  duration_minutes: number;
}

export interface PartyScheduleResponse {
  is_open: boolean;
  windows: PartyWindow[];
  current_window_end?: string | null;
  next_window_start?: string | null;
  admin_override?: boolean;
  admin_override_end?: string | null;
  matchmaking_queue_len?: number;
}

export interface PartyMember {
  user_id: number;
  name: string;
  br: number;
  class: string;
  ready: boolean;
}

export interface PartyRoomDetail {
  room_id: string;
  leader_id: number;
  members: (PartyMember | null)[];
  status: string;
  team_size: number;
  team_br: number;
  seconds_remaining: number;
  rematch_seconds_remaining?: number;
  countdown_seconds_remaining?: number;
  fight_starts_at?: number | null;
  match_queued_at?: number | null;
  auto_start_paused_after_cancel?: boolean;
}

export interface PartyMeResponse {
  status: "none" | "waiting" | "matchmaking" | "started" | "expired" | "rematch_pending" | "countdown" | "spectating";
  room?: PartyRoomDetail | null;
  battle_id?: number | null;
  info_messages?: string[];
  seconds_remaining?: number;
  expired_message?: string | null;
  party_reward_attempts_used?: number;
  party_reward_attempts_limit?: number;
  party_rewards_exhausted?: boolean;
}

export interface PartyStartFightResponse {
  status: string;
  message: string;
  battle_id?: number | null;
  info_messages?: string[];
  result?: string | null;
  room?: PartyRoomDetail | null;
}

export interface ArenaShopItem {
  item_id: string;
  name: string;
  insignia_cost: number;
  reward_type: string;
  reward_data: Record<string, unknown>;
  icon: string;
}

export interface LeagueStatusResponse {
  tier: string;
  points: number;
  next_tier: string | null;
  next_tier_at: number | null;
  week_start: string;
}

export const arenaApi = {
  getSoloRanks: () => api.get<SoloRanksResponse>("/arena/solo/ranks"),
  attackSolo: (targetRank: number, autoMode: string = "rotation") =>
    api.post<AttackSoloResponse>(`/arena/solo/attack/${targetRank}`, { auto_mode: autoMode }),
  skipCooldown: () => api.post<{ message: string; diamonds_spent: number; remaining_diamonds: number }>(
    "/arena/solo/skip-cooldown",
  ),

  getSchedule: () => api.get<PartyScheduleResponse>("/arena/party/schedule"),
  getPartyMe: () => api.get<PartyMeResponse>("/arena/party/me"),
  ackPartyBattle: () => api.post<{ message: string; status?: string; room?: PartyRoomDetail }>("/arena/party/ack"),
  leaveCurrentPartyRoom: () => api.delete<{ message: string; status?: string }>("/arena/party/me"),
  createPartyRoom: () =>
    api.post<{ room_id: string; message: string; room?: PartyRoomDetail }>("/arena/party/rooms"),
  getPartyRooms: () => api.get<{ rooms: PartyRoomDetail[] }>("/arena/party/rooms"),
  getPartyRoom: (roomId: string) => api.get<PartyRoomDetail>(`/arena/party/rooms/${roomId}`),
  joinPartyRoom: (roomId: string) =>
    api.post<{ message: string; room: PartyRoomDetail }>(`/arena/party/rooms/${roomId}/join`),
  leavePartyRoom: (roomId: string) =>
    api.delete<{ message: string; status?: string }>(`/arena/party/rooms/${roomId}/leave`),
  toggleReady: (roomId: string) =>
    api.post<{ message: string; room: PartyRoomDetail }>(`/arena/party/rooms/${roomId}/ready`),
  startFight: (roomId: string) =>
    api.post<PartyStartFightResponse>(`/arena/party/rooms/${roomId}/fight`),
  cancelCountdown: (roomId: string) =>
    api.post<{ message: string; room: PartyRoomDetail; status?: string }>(
      `/arena/party/rooms/${roomId}/cancel-countdown`,
    ),

  getShop: () => api.get<ArenaShopItem[]>("/arena/shop"),
  buyItem: (itemId: string, quantity: number = 1) =>
    api.post<{ message: string; insignia_spent: number; remaining_insignia: number }>(
      "/arena/shop/buy",
      { item_id: itemId, quantity },
    ),

  getLeagueStatus: () => api.get<LeagueStatusResponse>("/arena/league/status"),
  getLeagueTop: () => api.get<{ entries: unknown[]; my_rank: number | null }>("/arena/league/top"),
};
